import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { featureFlags } from '@/utils/featureFlags';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ReportsExportManager } from '@/components/organisation/ReportsExportManager';
import { ReportWarningsBanner } from '@/components/organisation/ReportWarningsBanner';
import SyncInProgressBanner from '@/components/organisation/SyncInProgressBanner';
import { useProgressiveReports } from '@/hooks/useProgressiveReports';
import { useTimezoneInfo } from '@/hooks/useTimezoneInfo';
import { Loader2, Calendar as CalendarIcon, Download, CheckCircle, Clock, Info, ChevronDown } from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import { format, subMonths } from 'date-fns';
import EditableSkuTable from '@/components/data-table/EditableSkuTable';
import SalesHistoryTable from '@/components/data-table/SalesHistoryTable';
import TimezoneBadge from '@/components/ui/timezone-badge';
import DataTableFooter from '@/components/data-table/DataTableFooter';
import MultiColumnFilter from '@/components/data-table/MultiColumnFilter';
import { FilterOperation } from '@/hooks/useConnectionData';

interface OrganisationReportsTabProps {
  organisation: any;
}

const computeDefaultSourceIds = (sources: Array<{ id: string; name: string; type: string }>): string[] => {
  const filteredSources = sources.filter(source => source.id !== 'All' && source.id !== 'none');
  const apiTypes = new Set(
    filteredSources
      .filter(source => !source.name.includes('CSV Import'))
      .map(source => source.type)
  );

  return filteredSources
    .filter(source => !(source.name.includes('CSV Import') && apiTypes.has(source.type)))
    .map(source => source.id);
};

const OrganisationReportsTab: React.FC<OrganisationReportsTabProps> = ({ organisation }) => {
  const [activeReportTab, setActiveReportTab] = useState('skus');
  const [activeFilters, setActiveFilters] = useState<Array<{ column: string; value: string; operation: FilterOperation }>>([]);
  const [pendingAsOfDate, setPendingAsOfDate] = useState<Date | undefined>(undefined);
  const [tabResetSignal, setTabResetSignal] = useState(0);
  const prevNonPaginationKeyRef = useRef<string | null>(null);

  // Helper function to get available data sources - each connection is a separate source
  const getAvailableDataSources = useCallback(() => {
    const sources: Array<{ id: string; name: string; type: string }> = [];
    const supportedConnectors = ['shopify', 'amazon'];

    if (organisation?.connections && Array.isArray(organisation.connections)) {
      const connections = organisation.connections;
      const supportedConnections = connections
        .filter((connection: any) => {
          const type = connection.type?.toLowerCase() || '';
          const service = connection.service?.toLowerCase() || '';
          const connectionId = connection.id || connection.connectionId || '';

          // Note: broken/error connections are kept — their synced data is still queryable

          // Exclude inventory-only CSV imports from reports
          if (connectionId.includes('_inventory_')) {
            return false;
          }

          // Check direct type matches
          if (supportedConnectors.some(supported => type.includes(supported))) {
            return true;
          }

          // Check CSV connections with ecommerce subtype
          if ((service === 'csv' || type === 'csv') &&
            connection.import_type === 'ecommerce' &&
            connection.ecommerce_subtype) {
            const subtype = connection.ecommerce_subtype.toLowerCase();
            return supportedConnectors.includes(subtype);
          }

          return false;
        })
        .map((connection: any, index: number) => {
          const type = connection.type?.toLowerCase() || '';
          const service = connection.service?.toLowerCase() || '';
          let connectionType = '';
          let isCsvImport = false;

          // Determine connection type
          if (type.includes('shopify')) {
            connectionType = 'Shopify';
          } else if (type.includes('amazon')) {
            connectionType = 'Amazon';
          } else if ((service === 'csv' || type === 'csv') &&
            connection.import_type === 'ecommerce' &&
            connection.ecommerce_subtype) {
            const subtype = connection.ecommerce_subtype.toLowerCase();
            isCsvImport = true;
            if (subtype === 'shopify') connectionType = 'Shopify';
            else if (subtype === 'amazon') connectionType = 'Amazon';
          }

          if (!connectionType) return null;

          // Create a display name that distinguishes CSV imports from direct connections
          const connectionId = connection.id || connection.connectionId;
          let displayName = connection.name || connectionType;

          // Add "CSV Import" suffix for CSV imports
          if (isCsvImport) {
            displayName = `${connectionType} CSV Import`;
          }

          return {
            id: connectionId,
            name: displayName,
            type: connectionType
          };
        })
        .filter(Boolean);

      // Disambiguate duplicate names by appending shop identifier from connectionId
      const nameCounts: Record<string, number> = {};
      supportedConnections.forEach((s: any) => {
        nameCounts[s.name] = (nameCounts[s.name] || 0) + 1;
      });

      const disambiguated = supportedConnections.map((s: any) => {
        if (nameCounts[s.name] > 1) {
          // Extract shop identifier from connectionId (e.g., "shopify_myshop_name" → "myshop name")
          const connId = s.id || '';
          const servicePrefix = s.type?.toLowerCase() || '';
          let suffix = connId.replace(new RegExp(`^${servicePrefix}_`, 'i'), '').replace(/_/g, ' ').trim();
          // For CSV imports, strip the csv-import- prefix
          if (suffix.startsWith('csv-import-') || suffix.startsWith('csv import ')) {
            suffix = suffix.replace(/^csv[- ]import[- ]/, '').trim();
          }
          if (suffix && suffix !== connId) {
            return { ...s, name: `${s.name} (${suffix})` };
          }
          // Fallback: append numeric index
          const dupes = supportedConnections.filter((d: any) => d.name === s.name);
          const idx = dupes.indexOf(s) + 1;
          return { ...s, name: `${s.name} (${idx})` };
        }
        return s;
      });

      sources.push(...disambiguated);

      // Add "All" option if there are multiple supported connections
      if (supportedConnections.length > 1) {
        sources.unshift({ id: 'All', name: 'All Sources', type: 'All' });
      }
    }

    return sources.length > 0 ? sources : [{ id: 'none', name: 'No supported connections', type: 'none' }];
  }, [organisation]);

  const getDataSourceDisplayText = () => {
    const sources = getAvailableDataSources().filter(s => s.id !== 'none');
    const selectedSources = filters.dataSources || [];

    if (selectedSources.length === 0) {
      return 'No sources selected';
    }

    const availableSources = sources.filter(s => s.id !== 'All');
    if (selectedSources.length === availableSources.length) {
      return 'All Sources';
    }

    if (selectedSources.length === 1) {
      const selected = sources.find(s => s.id === selectedSources[0]);
      return selected?.name || selectedSources[0];
    }

    return `${selectedSources.length} sources selected`;
  };

  const getLoadingSourceLabel = useCallback((sourceKey: string) => {
    if (sourceKey === 'aggregated') return 'All Sources';

    const availableSource = getAvailableDataSources().find(source => source.id === sourceKey);
    return availableSource?.name || sourceKey;
  }, [getAvailableDataSources]);

  const getConnectionIdForSource = (): string => {
    try {
      if (!organisation?.connections || !Array.isArray(organisation.connections)) return '';

      const selectedSources = filters.dataSources || [];
      // If multiple sources selected, return empty string (will be handled by progressive hook)
      if (selectedSources.length !== 1) return '';

      // Find connection by ID for single source
      const found = organisation.connections.find((conn: any) =>
        (conn.id === selectedSources[0] || conn.connectionId === selectedSources[0])
      );

      return found ? (found.id || found.connectionId || '') : '';
    } catch {
      return '';
    }
  };

  const [filters, setFilters] = useState({
    trailing12Months: true, // Always use trailing 12 months with precomputed tables
    dataSources: [] as string[], // Array of connection IDs
    pageSize: 100,
    metricType: 'GMV', // For sales history filtering
    page: 1,
    asOfDate: '' as string // Custom end date for trailing 12 months (empty = today)
  });

  // Pagination state
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalsData, setTotalsData] = useState<any>(null);

  // Memoized connection IDs for timezone info to avoid re-fetch loops
  const connectionIds = useMemo(() => {
    if (!organisation?.connections || !Array.isArray(organisation.connections)) return [];
    return organisation.connections
      .map((conn: any) => conn.id || conn.connectionId)
      .filter(Boolean);
  }, [organisation?.connections]);

  // Use timezone info hook
  const { timezoneInfo, loading: timezoneLoading, getTimezoneForConnection } = useTimezoneInfo(connectionIds);
  const [showExportManager, setShowExportManager] = useState(false);
  const [sourcesPopoverOpen, setSourcesPopoverOpen] = useState(false);
  const [pendingDataSources, setPendingDataSources] = useState<string[]>([]);

  // Use progressive reports hook
  const {
    data: reportData,
    loading,
    generating,
    generationReason,
    loadingBySource,
    completedSources,
    error,
    fetchReportData,
    totalRows: hookTotalRows,
    totalRowsKnown,
    totalsData: hookTotalsData,
    totalsUnavailable,
    warnings,
    inventorySourceApplied,
  } = useProgressiveReports({
    organisation,
    onDataUpdate: (data, totalRows, totals) => {
      // Update pagination metadata
      if (totalRows !== undefined) setTotalRows(totalRows);
      if (totals) setTotalsData(totals);
    }
  });

  // Update state when hook data changes
  React.useEffect(() => {
    if (hookTotalRows !== undefined) setTotalRows(hookTotalRows);
    if (hookTotalsData) setTotalsData(hookTotalsData);
  }, [hookTotalRows, hookTotalsData]);

  // Update default data sources when organisation changes
  // Smart default: prefer API connections over CSV when both exist for the same platform
  useEffect(() => {
    const defaultSourceIds = computeDefaultSourceIds(getAvailableDataSources());

    setFilters(prev => ({
      ...prev,
      trailing12Months: true,
      dataSources: defaultSourceIds,
      pageSize: 100,
      metricType: 'GMV',
      page: 1
    }));
  }, [organisation]);

  const resetAllFiltersForTabSwitch = () => {
    const defaultSourceIds = computeDefaultSourceIds(getAvailableDataSources());

    setActiveFilters([]);
    setPendingAsOfDate(undefined);
    setFilters({
      trailing12Months: true,
      dataSources: defaultSourceIds,
      pageSize: 100,
      metricType: 'GMV',
      page: 1,
      asOfDate: ''
    });
    setCurrentPage(1);
    setTabResetSignal(prev => prev + 1);
  };

  const handleReportTabChange = (nextTab: string) => {
    if (nextTab === activeReportTab) return;
    resetAllFiltersForTabSwitch();
    setActiveReportTab(nextTab);
  };

  // Convert activeFilters to columnFilters format for backend
  const getColumnFilters = () => {
    return activeFilters.map(filter => ({
      column: filter.column,
      value: filter.value,
      operation: filter.operation
    }));
  };

  useEffect(() => {
    // Guard: Don't fetch if data sources aren't ready yet
    if (!filters.dataSources || filters.dataSources.length === 0) return;

    const columnFilters = activeFilters.map(filter => ({
      column: filter.column,
      value: filter.value,
      operation: filter.operation
    }));

    // Reset pagination when report type or non-pagination filters change.
    // Keep page/pageSize changes fetch-only (no forced reset).
    const nonPaginationKey = JSON.stringify({
      activeReportTab,
      dataSources: filters.dataSources,
      trailing12Months: filters.trailing12Months,
      metricType: filters.metricType,
      asOfDate: filters.asOfDate,
      columnFilters,
    });

    const prevKey = prevNonPaginationKeyRef.current;
    const shouldResetPage = prevKey !== nonPaginationKey;
    prevNonPaginationKeyRef.current = nonPaginationKey;

    // Avoid double-fetch: if we need to reset to page 1 and we're not already there,
    // update state and let the effect re-run with the new page.
    if (shouldResetPage && (filters.page || 1) !== 1) {
      setFilters(prev => ({ ...prev, page: 1 }));
      setCurrentPage(1);
      return;
    }

    const effectivePage = shouldResetPage ? 1 : (filters.page || 1);

    const filtersWithColumnFilters = {
      ...filters,
      page: effectivePage,
      columnFilters,
    };

    if (shouldResetPage) {
      setCurrentPage(1);
    }

    fetchReportData(activeReportTab, filtersWithColumnFilters);
  }, [
    activeReportTab,
    fetchReportData,
    activeFilters,
    filters.dataSources,
    filters.trailing12Months,
    filters.metricType,
    filters.asOfDate,
    filters.page,
    filters.pageSize,
  ]);

  useEffect(() => {
    // "All time" is only valid for Sales History; reset to trailing mode on other report tabs.
    if (activeReportTab !== 'sales-history' && !filters.trailing12Months) {
      setFilters(prev => ({ ...prev, trailing12Months: true, page: 1 }));
      setCurrentPage(1);
    }
  }, [activeReportTab, filters.trailing12Months]);

  const handleSourcesPopoverOpenChange = (open: boolean) => {
    if (open) {
      setPendingDataSources([...(filters.dataSources || [])]);
    }
    setSourcesPopoverOpen(open);
  };

  const handlePendingDataSourceToggle = (sourceId: string) => {
    setPendingDataSources((currentSources) => {
      if (currentSources.includes(sourceId)) {
        const next = currentSources.filter((id) => id !== sourceId);
        if (next.length === 0) {
          return currentSources;
        }
        return next;
      }
      return [...currentSources, sourceId];
    });
  };

  const handleSelectAllSourcesDraft = () => {
    const sources = getAvailableDataSources().filter((s) => s.id !== 'All' && s.id !== 'none');
    setPendingDataSources(sources.map((s) => s.id));
  };

  const handleApplyDataSources = () => {
    if (pendingDataSources.length === 0) {
      return;
    }
    setFilters((prev) => ({ ...prev, dataSources: [...pendingDataSources] }));
    setSourcesPopoverOpen(false);
  };

  const handleCancelDataSources = () => {
    setSourcesPopoverOpen(false);
  };

  const renderReportControls = () => (
    <div className="space-y-4 mb-6">
      {/* Header with Export Manager */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {activeReportTab === 'skus' ? '∑: Products' :
              activeReportTab === 'fixed-costs' ? 'Fixed Costs' : 'Sales History'}
          </h3>
          {activeReportTab === 'skus' && inventorySourceApplied != null && (
            <Badge
              variant="outline"
              className="text-xs text-muted-foreground bg-muted/40 border-border"
            >
              {`Inventory source: ${inventorySourceApplied === 'amazon'
                  ? 'Amazon'
                  : inventorySourceApplied === 'amazon_csv'
                    ? 'Amazon CSV'
                    : inventorySourceApplied === 'shopify'
                      ? 'Shopify'
                      : inventorySourceApplied === 'shopify_csv'
                        ? 'Shopify CSV'
                        : 'Per-connection'
                }`}
            </Badge>
          )}

          {/* Display timezone information for selected data sources */}
          {(() => {
            const selectedSources = filters.dataSources || [];
            if (selectedSources.length > 0) {
              // Get timezones for selected sources
              const selectedTimezones = selectedSources
                .map(id => getTimezoneForConnection(id))
                .filter(Boolean)
                .reduce((unique, tz) => {
                  const key = `${tz!.detected_timezone}_${tz!.timezone_offset}`;
                  if (!unique.find(u => `${u.detected_timezone}_${u.timezone_offset}` === key)) {
                    unique.push(tz!);
                  }
                  return unique;
                }, [] as any[]);

              if (selectedTimezones.length > 0) {
                const hasMultipleTimezones = selectedTimezones.length > 1;
                return (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">
                      {hasMultipleTimezones ? 'Mixed Timezones:' : 'Data Timezone:'}
                    </span>
                    <div className="flex items-center gap-1">
                      {selectedTimezones.map((tz, index) => (
                        <TimezoneBadge
                          key={index}
                          timezone={tz.detected_timezone}
                          offset={tz.timezone_offset}
                          size="sm"
                        />
                      ))}
                      {hasMultipleTimezones && (
                        <Badge variant="destructive" className="text-xs ml-2">
                          ⚠️ Mixed
                        </Badge>
                      )}
                    </div>
                  </div>
                );
              }
            }
            return null;
          })()}
        </div>

        {/* Export Manager Button */}
        <Button
          variant="outline"
          onClick={() => setShowExportManager(true)}
          className="text-sm"
        >
          <Download className="h-4 w-4 mr-2" />
          Export Manager
        </Button>
      </div>

      {/* Filter Controls - All on same row */}
      <div className="flex items-center gap-2 flex-wrap">
        <Popover open={sourcesPopoverOpen} onOpenChange={handleSourcesPopoverOpenChange}>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-fit min-w-40 justify-between">
              <span className="text-sm">{getDataSourceDisplayText()}</span>
              <ChevronDown className="ml-2 h-4 w-4 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-0 bg-popover" align="start">
            <div className="p-4 space-y-3">
              <div className="flex items-center justify-between pb-2 border-b">
                <span className="text-sm font-semibold">Select Sources</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  type="button"
                  onClick={handleSelectAllSourcesDraft}
                >
                  Select All
                </Button>
              </div>
              {getAvailableDataSources()
                .filter(s => s.id !== 'All' && s.id !== 'none')
                .map((source) => {
                  const isOnlySelected =
                    pendingDataSources.length === 1 && pendingDataSources.includes(source.id);
                  return (
                    <div key={source.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={source.id}
                        checked={pendingDataSources.includes(source.id)}
                        disabled={isOnlySelected}
                        onCheckedChange={() => handlePendingDataSourceToggle(source.id)}
                      />
                      <label
                        htmlFor={source.id}
                        className={`text-sm font-normal leading-none ${isOnlySelected ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                      >
                        {source.name}
                      </label>
                    </div>
                  );
                })}
              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="text-xs"
                  onClick={handleCancelDataSources}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  size="sm"
                  className="text-xs"
                  onClick={handleApplyDataSources}
                  disabled={pendingDataSources.length === 0}
                >
                  Apply
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>

        {/* Date range mode for Sales History tab */}
        {activeReportTab === 'sales-history' && (
          <div className="flex items-center gap-2">
            <Select
              value={filters.trailing12Months ? 'trailing' : 'all'}
              onValueChange={(value) => {
                if (value === 'all') {
                  setFilters(prev => ({ ...prev, trailing12Months: false, asOfDate: '', page: 1 }));
                  setCurrentPage(1);
                } else {
                  setFilters(prev => ({ ...prev, trailing12Months: true, page: 1 }));
                  setCurrentPage(1);
                }
              }}
            >
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="trailing">Trailing 12 months</SelectItem>
                <SelectItem value="all">All time</SelectItem>
              </SelectContent>
            </Select>

            {/* As-of date picker only shown when trailing 12 months is active */}
            {filters.trailing12Months && (
              <Popover onOpenChange={(open) => {
                if (open) {
                  setPendingAsOfDate(filters.asOfDate ? new Date(filters.asOfDate + 'T12:00:00') : undefined);
                }
              }}>
                <PopoverTrigger asChild>
                  <Button variant={filters.asOfDate ? 'default' : 'outline'} className="text-sm">
                    <CalendarIcon className="h-4 w-4 mr-2" />
                    {filters.asOfDate
                      ? `As of ${format(new Date(filters.asOfDate + 'T12:00:00'), 'MMM d, yyyy')}`
                      : 'As of today'
                    }
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-4 bg-popover" align="start">
                  <div className="space-y-3">
                    <div className="text-sm font-semibold">Trailing 12 months ending on:</div>
                    <Calendar
                      mode="single"
                      selected={pendingAsOfDate}
                      onSelect={setPendingAsOfDate}
                      disabled={(date) => date > new Date()}
                      initialFocus
                      className="p-3 pointer-events-auto rounded-md border"
                    />
                    {pendingAsOfDate && (
                      <div className="text-xs text-muted-foreground text-center">
                        {format(subMonths(pendingAsOfDate, 12), 'MMM d, yyyy')} → {format(pendingAsOfDate, 'MMM d, yyyy')}
                      </div>
                    )}
                    <div className="flex items-center justify-between gap-2 pt-1">
                      {(filters.asOfDate || pendingAsOfDate) ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 text-xs"
                          onClick={() => {
                            setPendingAsOfDate(undefined);
                            setFilters(prev => ({ ...prev, asOfDate: '', page: 1 }));
                            setCurrentPage(1);
                          }}
                        >
                          Reset to today
                        </Button>
                      ) : <div />}
                      <Button
                        size="sm"
                        className="h-8"
                        disabled={!pendingAsOfDate}
                        onClick={() => {
                          if (pendingAsOfDate) {
                            const dateStr = format(pendingAsOfDate, 'yyyy-MM-dd');
                            setFilters(prev => ({ ...prev, asOfDate: dateStr, page: 1 }));
                            setCurrentPage(1);
                          }
                        }}
                      >
                        Apply
                      </Button>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            )}
          </div>
        )}

        {/* As-of date picker for SKUs tab */}
        {activeReportTab === 'skus' && (
          <Popover onOpenChange={(open) => {
            if (open) {
              setPendingAsOfDate(filters.asOfDate ? new Date(filters.asOfDate + 'T12:00:00') : undefined);
            }
          }}>
            <PopoverTrigger asChild>
              <Button variant={filters.asOfDate ? 'default' : 'outline'} className="text-sm">
                <CalendarIcon className="h-4 w-4 mr-2" />
                {filters.asOfDate
                  ? `Trailing 12 months as of ${format(new Date(filters.asOfDate + 'T12:00:00'), 'MMM d, yyyy')}`
                  : 'Trailing 12 months'
                }
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-4 bg-popover" align="start">
              <div className="space-y-3">
                <div className="text-sm font-semibold">Trailing 12 months ending on:</div>
                <Calendar
                  mode="single"
                  selected={pendingAsOfDate}
                  onSelect={setPendingAsOfDate}
                  disabled={(date) => date > new Date()}
                  initialFocus
                  className="p-3 pointer-events-auto rounded-md border"
                />
                {pendingAsOfDate && (
                  <div className="text-xs text-muted-foreground text-center">
                    {format(subMonths(pendingAsOfDate, 12), 'MMM d, yyyy')} → {format(pendingAsOfDate, 'MMM d, yyyy')}
                  </div>
                )}
                <div className="flex items-center justify-between gap-2 pt-1">
                  {(filters.asOfDate || pendingAsOfDate) ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 text-xs"
                      onClick={() => {
                        setPendingAsOfDate(undefined);
                        setFilters(prev => ({ ...prev, asOfDate: '', page: 1 }));
                        setCurrentPage(1);
                      }}
                    >
                      Reset to today
                    </Button>
                  ) : <div />}
                  <Button
                    size="sm"
                    className="h-8"
                    disabled={!pendingAsOfDate}
                    onClick={() => {
                      if (pendingAsOfDate) {
                        const dateStr = format(pendingAsOfDate, 'yyyy-MM-dd');
                        setFilters(prev => ({ ...prev, asOfDate: dateStr, page: 1 }));
                        setCurrentPage(1);
                      }
                    }}
                  >
                    Apply
                  </Button>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        )}

        {/* Trailing 12 months badge for fixed-costs tab */}
        {activeReportTab === 'fixed-costs' && (
          <Button
            variant="default"
            onClick={() => { }}
            className="text-sm"
            disabled={true}
          >
            <CalendarIcon className="h-4 w-4 mr-2" />
            Trailing 12 months
          </Button>
        )}

        {/* Multi-column filter for SKUs tab */}
        {activeReportTab === 'skus' && (
          <MultiColumnFilter
            columns={columns}
            activeFilters={activeFilters}
            onAddFilter={handleAddFilter}
            onClearAllFilters={handleClearAllFilters}
            tableData={reportData || []}
          />
        )}

        {/* Metric Type Filter - only for Sales History */}
        {activeReportTab === 'sales-history' && (
          <Select value={filters.metricType} onValueChange={(value) => {
            const newFilters = { ...filters, metricType: value };
            setFilters(newFilters);
          }}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="GMV">∑ GMV</SelectItem>
              <SelectItem value="NMV">∑ NMV</SelectItem>
              <SelectItem value="Quantity">∑ Quantity</SelectItem>
              <SelectItem value="Payout">∑ Payout</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Metric Description - only for Sales History */}
      {activeReportTab === 'sales-history' && (
        <div className="bg-muted/50 border border-border rounded-lg p-3.5">
          <div className="flex items-start gap-2.5">
            <Info className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h4 className="font-semibold text-sm mb-1 text-foreground">
                {filters.metricType === 'GMV' && '∑ GMV'}
                {filters.metricType === 'NMV' && '∑ NMV'}
                {filters.metricType === 'Quantity' && '∑ Quantity'}
                {filters.metricType === 'Payout' && '∑ Payout'}
              </h4>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {filters.metricType === 'GMV' && 'Gross Merchandise Value: Total customer spend before discounts and fees. Calculated as: Quantity × Unit Price + Shipping & Gift Wrap.'}
                {filters.metricType === 'NMV' && 'Net Merchandise Value: Your true net product revenue. Calculated as: Quantity × Unit Price + Shipping & Gift Wrap, if included - Discounts - Refunds.'}
                {filters.metricType === 'Quantity' && 'Total number of units sold during the period.'}
                {filters.metricType === 'Payout' && 'Net Revenue After Fees: The actual amount deposited to your account by the ecommerce system (Amazon/Shopify) after all fees, taxes, deductions, and refunds.'}
              </p>
            </div>
          </div>
        </div>
      )}

    </div>
  );

  const [showGlobalSettings, setShowGlobalSettings] = useState(false);

  // Extract columns from report data - must be before filter functions
  const columns = useMemo(() => {
    if (!reportData || reportData.length === 0) return [];
    return Object.keys(reportData[0]).filter(key =>
      key !== 'sourceBreakdown' &&
      key !== 'data_source' &&
      key !== 'constituent_skus' &&
      key !== 'inventory_source_origin' &&
      key !== 'identifier_missing' &&
      key !== 'Marketplace' &&
      key !== 'marketplace' &&
      key !== 'total_count' &&
      key !== 'DR: SKU %' &&
      key !== '∑: NERV $'
    );
  }, [reportData]);

  // Filter handler functions
  const handleAddFilter = (column: string, value: string, operation: FilterOperation) => {
    setActiveFilters(prev => {
      const existingIndex = prev.findIndex(
        filter => filter.column === column && filter.operation === operation && filter.value === value
      );
      if (existingIndex >= 0) {
        return prev.filter((_, i) => i !== existingIndex);
      }
      return [...prev, { column, value, operation }];
    });
  };

  const handleRemoveFilter = (column: string, value: string, operation: FilterOperation) => {
    setActiveFilters(prev =>
      prev.filter(
        filter => !(filter.column === column && filter.value === value && filter.operation === operation)
      )
    );
  };

  const handleClearAllFilters = () => {
    setActiveFilters([]);
  };

  const handlePageSizeChange = (value: string) => {
    const newFilters = {
      ...filters,
      pageSize: parseInt(value),
      page: 1
    };
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    const newFilters = { ...filters, page };
    setFilters(newFilters);
  };

  const renderPageSizeControl = () => (
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-4 text-sm text-gray-600">
        <span>Page size:</span>
        <Select value={filters.pageSize.toString()} onValueChange={handlePageSizeChange}>
          <SelectTrigger className="w-20">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="100">100</SelectItem>
            <SelectItem value="250">250</SelectItem>
            <SelectItem value="500">500</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {activeReportTab === 'skus' && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowGlobalSettings(!showGlobalSettings)}
        >
          {showGlobalSettings ? 'Hide' : 'Configure'} Global Settings
        </Button>
      )}
    </div>
  );

  const renderDataTable = () => {
    // Show generating banner when backend is preparing data
    if (generating) {
      const isInventorySourceRegen = generationReason === 'inventory_source_changed';
      return (
        <div className="border border-border rounded-lg p-8 text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="font-medium text-foreground">
            {isInventorySourceRegen
              ? 'Regenerating report because the inventory source changed...'
              : 'Report data is being prepared...'}
          </p>
          <p className="text-sm text-muted-foreground mt-2">
            {isInventorySourceRegen
              ? 'This runs automatically and may take a few minutes. The page will update when ready.'
              : 'This may take a while. The page will update automatically when ready.'}
          </p>
        </div>
      );
    }

    // Show loading only if no data is available yet and we're still loading
    if (loading && (!reportData || reportData.length === 0)) {
      return (
        <div className="border border-border rounded-lg p-8 text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p>Loading report data...</p>
        </div>
      );
    }

    // Prefer showing errors (including backend timeouts) over empty-state messaging
    if (error && (!reportData || reportData.length === 0)) {
      const message = error ?? 'Failed to load report data. Please retry.';

      return (
        <div className="border border-border rounded-lg p-8 text-center text-muted-foreground space-y-4">
          <div>
            <p className="font-medium text-foreground">We couldn’t load this report.</p>
            <p className="text-sm mt-2">{message}</p>
          </div>
          <div className="flex items-center justify-center gap-2">
            <Button
              variant="default"
              size="sm"
              onClick={() => fetchReportData(activeReportTab, { ...filters, columnFilters: getColumnFilters() })}
            >
              Retry
            </Button>
          </div>
        </div>
      );
    }

    if (!reportData || reportData.length === 0) {
      return (
        <div className="border border-border rounded-lg p-8 text-center text-muted-foreground">
          <p>No data currently available for this report.</p>
          <p className="text-sm mt-2">Connect further data sources or wait for existing ones to finish uploading/syncing.</p>
        </div>
      );
    }

    // Data is already filtered and paginated by backend
    const visibleRows = reportData;

    // Check if we're still loading data from any source (for multiple sources mode)
    const isProgressiveLoading = filters.dataSources.length > 1 &&
      Object.values(loadingBySource).some(loading => loading);

    // Check if totals are still loading (for progressive loading mode)
    const areTotalsLoading = isProgressiveLoading;

    // Use EditableSkuTable for SKUs tab only
    if (activeReportTab === 'skus') {
      // Handle empty filtered results
      if (visibleRows.length === 0) {
        return (
          <div className="border border-gray-200 rounded-lg p-8 text-center text-gray-500">
            <p>No data matches the current filters</p>
            <p className="text-sm mt-2">Try adjusting or clearing your filters</p>
          </div>
        );
      }

      // Add DR: SKU % and ∑: NERV $ columns only for SKUs data
      const enrichedData = visibleRows.map(row => ({
        ...row,
        'DR: SKU %': row['DR: SKU %'] || '0.00',
        '∑: NERV $': row['∑: NERV $'] || '0.00'
      }));

      // Filter out Marketplace, Shipping Cost $, Connection Id columns
      // Always hide NEV projection columns, conditionally show NERV projections
      const alwaysHiddenColumns = [
        'NEV (In 1 Month)', 'NEV (In 3 Months)', 'NEV (In 6 Months)',
        'NEV (in 1 month)', 'NEV (in 3 months)', 'NEV (in 6 months)',
        'Velocity (units/day)', 'Days of Stock',
        'NERV 30d Proj $', 'NERV 60d Proj $', 'NERV 90d Proj $'
      ];
      const nervProjectionColumns = [
        'NERV (in 1 month)', 'NERV (in 3 months)', 'NERV (in 6 months)'
      ];
      const tableColumns = Object.keys(enrichedData[0]).filter(col => {
        // Always exclude these columns
        if (col === 'Marketplace' || col === 'Shipping Cost $' || col === 'Connection Id' || col === 'connection_id' || col === 'constituent_skus' || col === 'identifier_missing' || col === 'master_product_id' || col === 'Master Product Id' || col === 'inventory_source_origin' || col === 'Inventory Source Origin') {
          return false;
        }
        // Always hide NEV projection columns
        if (alwaysHiddenColumns.includes(col)) {
          return false;
        }
        // Conditionally exclude NERV projection columns based on feature flag
        if (!featureFlags.showReportsNervProjections && nervProjectionColumns.includes(col)) {
          return false;
        }
        return true;
      });

      // Only reorder NERV projection columns, keep all others in original order
      const nervColumnsOrder = ['∑: NERV $', 'NERV (in 6 months)', 'NERV (in 3 months)', 'NERV (in 1 month)'];

      // Find where the first NERV column appears in the original order
      const nervBaseIndex = tableColumns.findIndex(col => nervColumnsOrder.includes(col));

      let sortedColumns = [...tableColumns];

      if (nervBaseIndex !== -1) {
        // Remove all NERV columns from their current positions
        const nonNervColumns = tableColumns.filter(col => !nervColumnsOrder.includes(col));
        const existingNervColumns = nervColumnsOrder.filter(col => tableColumns.includes(col));

        // Find the insertion point based on where NERV columns should go
        const insertIndex = nonNervColumns.findIndex((col) => {
          const originalIdx = tableColumns.indexOf(col);
          return originalIdx > nervBaseIndex;
        });

        if (insertIndex !== -1) {
          sortedColumns = [
            ...nonNervColumns.slice(0, insertIndex),
            ...existingNervColumns,
            ...nonNervColumns.slice(insertIndex)
          ];
        } else {
          sortedColumns = [...nonNervColumns, ...existingNervColumns];
        }
      }
      const connectionId = getConnectionIdForSource();

      return (
        <div className="space-y-4">
          <ReportWarningsBanner warnings={warnings} />
          <EditableSkuTable
            data={enrichedData}
            columns={sortedColumns}
            organisationId={organisation?.id}
            connectionId={connectionId}
            isAdmin={true} // TODO: Get this from user context
            organisation={organisation}
            onDataRefresh={() => fetchReportData(activeReportTab, { ...filters, columnFilters: getColumnFilters() })}
            showGlobalSettings={showGlobalSettings}
            onGlobalSettingsToggle={() => setShowGlobalSettings(false)}
            isLoading={isProgressiveLoading}
            areTotalsLoading={areTotalsLoading}
            totalsData={totalsData}
            totalsUnavailable={totalsUnavailable}
          />
        </div>
      );
    }

    // Use specialized SalesHistoryTable for sales-history tab
    if (activeReportTab === 'sales-history') {
      // Handle empty filtered results
      if (visibleRows.length === 0) {
        return (
          <div className="border border-gray-200 rounded-lg p-8 text-center text-gray-500">
            <p>No data matches the current filters</p>
            <p className="text-sm mt-2">Try adjusting or clearing your filters</p>
          </div>
        );
      }

      return (
        <div className="space-y-4">
          <SalesHistoryTable
            data={visibleRows}
            loading={loading && (!reportData || reportData.length === 0)}
            resetSignal={tabResetSignal}
            timezoneInfo={(() => {
              if (filters.dataSources.length > 1) {
                // For multiple sources mode, return the first available timezone or null
                // SalesHistoryTable will need to be updated to handle multiple timezones
                const allTimezones = connectionIds
                  .map(id => getTimezoneForConnection(id))
                  .filter(Boolean);
                return allTimezones.length > 0 ? allTimezones[0] : null;
              } else {
                const connectionId = getConnectionIdForSource();
                return getTimezoneForConnection(connectionId);
              }
            })()}
          />

          {/* Progressive loading indicator for "All" mode in Sales History tab */}
          {isProgressiveLoading && (
            <div className="border border-gray-200 rounded-lg p-6 text-center bg-gray-50">
              <div className="flex items-center justify-center gap-4">
                <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                <div className="flex flex-col items-start">
                  <p className="text-sm font-medium text-gray-700">Loading additional data sources...</p>
                  <div className="flex items-center gap-2 mt-1">
                    {Object.entries(loadingBySource).map(([source, isLoading]) => (
                      <Badge
                        key={source}
                        variant={isLoading ? "secondary" : "default"}
                        className="text-xs"
                      >
                        {isLoading ? (
                          <>
                            <Clock className="h-3 w-3 mr-1" />
                            {getLoadingSourceLabel(source)}
                          </>
                        ) : (
                          <>
                            <CheckCircle className="h-3 w-3 mr-1" />
                            {getLoadingSourceLabel(source)}
                          </>
                        )}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    }

    // For other tabs (fixed-costs), use regular table without DR: SKU % and ∑: NERV $ columns
    // Handle empty filtered results
    if (visibleRows.length === 0) {
      return (
        <div className="border border-gray-200 rounded-lg p-8 text-center text-gray-500">
          <p>No data matches the current filters</p>
          <p className="text-sm mt-2">Try adjusting or clearing your filters</p>
        </div>
      );
    }

    const tableColumns = Object.keys(visibleRows[0]).filter(col => col !== 'connection_id' && col !== 'Marketplace' && col !== 'marketplace');

    // Sort by data_source first, then by type
    const sortedRows = [...visibleRows].sort((a, b) => {
      const dataSourceA = a.data_source || a['Data Source'] || '';
      const dataSourceB = b.data_source || b['Data Source'] || '';
      const typeA = a.type || a['Type'] || '';
      const typeB = b.type || b['Type'] || '';

      // First sort by data source
      const sourceCompare = dataSourceA.localeCompare(dataSourceB);
      if (sourceCompare !== 0) return sourceCompare;

      // Then sort by type within the same data source
      return typeA.localeCompare(typeB);
    });

    return (
      <div className="space-y-4">
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <Table containerClassName="max-h-[600px] overflow-auto">
            <TableHeader>
              <TableRow>
                {tableColumns.map((column) => (
                  <TableHead key={column} className="sticky top-0 z-10 bg-white font-semibold border-b-2 border-gray-200 whitespace-nowrap">
                    {column.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedRows.map((row, index) => (
                <TableRow key={index}>
                  {tableColumns.map((column) => (
                    <TableCell key={column}>
                      {typeof row[column] === 'object' ? JSON.stringify(row[column]) : row[column]?.toString() || ''}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Progressive loading indicator for "All" mode */}
        {isProgressiveLoading && (
          <div className="border border-gray-200 rounded-lg p-6 text-center bg-gray-50">
            <div className="flex items-center justify-center gap-4">
              <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
              <div className="flex flex-col items-start">
                <p className="text-sm font-medium text-gray-700">Loading additional data sources...</p>
                <div className="flex items-center gap-2 mt-1">
                  {Object.entries(loadingBySource).map(([source, isLoading]) => (
                    <Badge
                      key={source}
                      variant={isLoading ? "secondary" : "default"}
                      className="text-xs"
                    >
                      {isLoading ? (
                        <>
                          <Clock className="h-3 w-3 mr-1" />
                          {source}
                        </>
                      ) : (
                        <>
                          <CheckCircle className="h-3 w-3 mr-1" />
                          {source}
                        </>
                      )}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <SyncInProgressBanner
        organisation={organisation}
        selectedConnectorIds={filters.dataSources.length > 0 ? filters.dataSources : undefined}
      />
      <Tabs value={activeReportTab} onValueChange={handleReportTabChange} className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="skus">∑: Products</TabsTrigger>
          <TabsTrigger value="fixed-costs">Fixed Costs</TabsTrigger>
          <TabsTrigger value="sales-history">Sales History</TabsTrigger>
        </TabsList>

        <TabsContent value="skus" className="mt-6">
          <div className="space-y-4">
            {renderReportControls()}
            {renderPageSizeControl()}
            {renderDataTable()}
            {!generating && !(loading && (!reportData || reportData.length === 0)) && (
              <DataTableFooter
                totalCount={(activeFilters.length === 0 && totalRowsKnown) ? totalRows : null}
                filteredCount={activeFilters.length > 0 ? (reportData?.length || 0) : totalRows}
                currentPage={currentPage}
                totalPages={(activeFilters.length === 0 && totalRowsKnown) ? Math.ceil(totalRows / filters.pageSize) : 1}
                onPageChange={handlePageChange}
                pageSize={filters.pageSize}
              />
            )}
          </div>
        </TabsContent>

        <TabsContent value="fixed-costs" className="mt-6">
          <div className="space-y-4">
            {renderReportControls()}
            {renderPageSizeControl()}
            {renderDataTable()}
            {!generating && !(loading && (!reportData || reportData.length === 0)) && (
              <DataTableFooter
                totalCount={(activeFilters.length === 0 && totalRowsKnown) ? totalRows : null}
                filteredCount={activeFilters.length > 0 ? (reportData?.length || 0) : totalRows}
                currentPage={currentPage}
                totalPages={(activeFilters.length === 0 && totalRowsKnown) ? Math.ceil(totalRows / filters.pageSize) : 1}
                onPageChange={handlePageChange}
                pageSize={filters.pageSize}
              />
            )}
          </div>
        </TabsContent>

        <TabsContent value="sales-history" className="mt-6">
          <div className="space-y-4">
            {renderReportControls()}
            {renderPageSizeControl()}
            {renderDataTable()}
            {!generating && !(loading && (!reportData || reportData.length === 0)) && (
              <DataTableFooter
                totalCount={(activeFilters.length === 0 && totalRowsKnown) ? totalRows : null}
                filteredCount={activeFilters.length > 0 ? (reportData?.length || 0) : totalRows}
                currentPage={currentPage}
                totalPages={(activeFilters.length === 0 && totalRowsKnown) ? Math.ceil(totalRows / filters.pageSize) : 1}
                onPageChange={handlePageChange}
                pageSize={filters.pageSize}
              />
            )}
          </div>
        </TabsContent>
      </Tabs>

      <ReportsExportManager
        organisation={organisation}
        isOpen={showExportManager}
        onClose={() => setShowExportManager(false)}
        activeReportTab={activeReportTab}
        filters={filters}
      />
    </div>
  );
};

export default OrganisationReportsTab;